import { useState, useEffect } from "react";
import { mailingApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Copy, Loader2, FileText, Eye, Clock, MousePointerClick } from "lucide-react";

function formatDate(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return null;
  }
}

export function TemplateCard({ template, onEdit, onDelete, onDuplicate, onPreview, duplicating, stats }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await mailingApi.previewHtml({
          subject: template.subject || template.name,
          content_type: template.content_type || "text",
          blocks: template.blocks,
          message: template.body || "",
          hero_image: template.hero_image || null,
          preheader: template.preheader || null,
        });
        if (active) setHtml(res.data.html || "");
      } catch {
        if (active) setHtml("");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // Re-render bei jeder Änderung an HTML/Logo/Hero/Blöcken/Betreff
  }, [template.id, template.name, template.subject, template.body, template.hero_image, template.preheader, template.blocks, template.content_type, template.updated_at]);

  const isFinal = (template.status || "Entwurf") === "Final";
  const changedDate = formatDate(template.updated_at || template.created_at);

  return (
    <div className="rounded-lg border overflow-hidden bg-card group flex flex-col" data-testid={`template-card-${template.id}`}>
      <button
        type="button"
        onClick={() => onPreview && onPreview(template, html)}
        className="relative h-44 bg-[#eef1f4] overflow-hidden border-b block w-full cursor-pointer"
        title="Große Vorschau öffnen"
        data-testid={`template-preview-open-${template.id}`}
      >
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : html ? (
          <div className="absolute top-0 left-0" style={{ width: "600px", transform: "scale(0.42)", transformOrigin: "top left" }}>
            <iframe title={template.name} srcDoc={html} sandbox="" scrolling="no"
              style={{ width: "600px", height: "700px", border: 0, pointerEvents: "none" }} />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><FileText className="w-8 h-8" /></div>
        )}
        <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 rounded-full bg-background/95 border px-3 py-1.5 text-xs font-medium shadow-sm">
            <Eye className="w-3.5 h-3.5" /> Große Vorschau
          </span>
        </span>
        <span className="absolute top-2 left-2 flex items-center gap-1.5">
          <Badge
            variant="outline"
            className={`text-[10px] shrink-0 border ${isFinal ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
            data-testid={`template-status-${template.id}`}
          >
            {isFinal ? "Final" : "Entwurf"}
          </Badge>
        </span>
      </button>
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium truncate flex items-center gap-2">
              {template.name}
              {template.is_default && <Badge variant="outline" className="text-[10px] shrink-0">Standard</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{template.subject || "—"}</p>
          </div>
        </div>
        {changedDate && (
          <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1" data-testid={`template-changed-${template.id}`}>
            <Clock className="w-3 h-3" /> Geändert: {changedDate}
          </p>
        )}
        {stats && stats.sent > 0 ? (
          <div className="flex items-center gap-2 mt-1.5 text-[11px]" data-testid={`template-stats-${template.id}`}>
            <span className="inline-flex items-center gap-1 text-emerald-700" title={`${stats.opens} Öffnungen von ${stats.sent} zugestellten`}>
              <Eye className="w-3 h-3" /> {stats.open_rate != null ? `${stats.open_rate}%` : "–"} geöffnet
            </span>
            <span className="inline-flex items-center gap-1 text-blue-700" title={`${stats.clicks} Klicks von ${stats.sent} zugestellten`}>
              <MousePointerClick className="w-3 h-3" /> {stats.click_rate != null ? `${stats.click_rate}%` : "–"} geklickt
            </span>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground mt-1.5" data-testid={`template-stats-empty-${template.id}`}>Noch keine Versanddaten</p>
        )}
        <div className="flex gap-1 justify-end mt-2 pt-2 border-t">
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Bearbeiten" onClick={() => onEdit(template)} data-testid={`template-edit-${template.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplizieren" disabled={duplicating} onClick={() => onDuplicate && onDuplicate(template)} data-testid={`template-duplicate-${template.id}`}>
            {duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Löschen" onClick={() => onDelete(template)} data-testid={`template-delete-${template.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
